import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import cn from 'classnames';
import styles from './LineChart.module.css';
import ReactDOM from 'react-dom';
import Skeleton from '../../../Skeleton/Skeleton';
import CustomDatePicker from '../../../../SharedComponents/CustomDatePicker/CustomDatePicker';
import { cleanValue, formatYAxisLabels, calculateFractionalDigits, getExponent } from './LineChart.helpers';

type LineChartProps = {
    title?: string;
    points: Point[];
    loading?: boolean;
    error?: boolean;
    containerClassName?: string;
    height?: number;
    dateFrom?: Date;
    onDateFromChange?: (date: Date) => void;
    dateTo?: Date;
    onDateToChange?: (date: Date) => void;
    minDate?: Date;
    renderTooltip?: (item: Point) => React.ReactNode;
    beginAtZero?: boolean;
    enableDateRange?: boolean;
};

export type Point = { x: string; y: number };
const TOOLTIP_WIDTH_IN_PX = 120;
const noop = () => {};
const now = new Date();

const LineChart = ({
    points,
    title,
    containerClassName,
    height = 100,
    loading,
    error,
    dateFrom,
    onDateFromChange = noop,
    dateTo,
    onDateToChange = noop,
    minDate,
    renderTooltip,
    beginAtZero = false,
    enableDateRange = false,
}: LineChartProps) => {
    const [tooltipParams, setTooltipParams] = useState<{ x: number; y: number; transformX: number } | null>(null);
    const [tooltipItem, setTooltipItem] = useState<Point | null>(null);
    const renderDefaultTooltip = () => tooltipItem && <div className={styles.tooltipContent}>{tooltipItem.y}</div>;
    const tooltip = renderTooltip ?? renderDefaultTooltip;

    const data = (canvas: HTMLCanvasElement) => {
        const ctx = canvas.getContext('2d');
        const gradient = ctx?.createLinearGradient(0, height * 4, 0, 0);
        gradient?.addColorStop(0, '#AE46FF00');
        gradient?.addColorStop(0.65, '#C338F420');
        gradient?.addColorStop(1, '#3C38F420');

        return {
            datasets: [
                {
                    data: points,
                    borderColor: '#7A61DF',
                    backgroundColor: gradient,
                    pointBackgroundColor: '#7A61DF',
                    pointBorderColor: 'white',
                    pointBorderWidth: 0,
                    pointHoverBorderWidth: 3,
                    pointHoverBorderColor: 'white',
                },
            ],
        };
    };

    const options = useMemo(() => {
        const yValues = points.map((point) => point.y);
        const maxValue = Math.max(...yValues);
        const minValue = Math.min(...yValues);
        const minMaxDiff = maxValue - minValue;
        const stepSize =
            isFinite(minMaxDiff) && minMaxDiff >= 0 ? undefined : Math.pow(10, getExponent(yValues[0]) - 1);

        return {
            legend: {
                display: false,
            },
            elements: {
                point: {
                    radius: 6,
                    hoverRadius: 7,
                },
            },
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero,
                            fontColor: '#90A0B7',
                            maxTicksLimit: 7,
                            stepSize,
                            callback: (value: number) => {
                                const cleanedValue = cleanValue(value);
                                if (new Set(yValues).size < 2) {
                                    return formatYAxisLabels(cleanedValue, 1);
                                }
                                const labelsFD = calculateFractionalDigits(yValues);
                                return formatYAxisLabels(cleanedValue, labelsFD);
                            },
                        },
                        gridLines: {
                            drawBorder: false,
                            borderDash: [5, 5],
                            color: '#90A0B7',
                        },
                    },
                ],
                xAxes: [
                    {
                        type: 'time',
                        ticks: {
                            fontColor: '#90A0B7',
                            autoSkip: true,
                            maxTicksLimit: 30,
                        },
                        time: {
                            unit: 'day',
                            displayFormats: {
                                day: 'MMM D',
                            },
                        },
                        gridLines: {
                            display: false,
                            color: '#90A0B7',
                        },
                    },
                ],
            },
            tooltips: {
                enabled: false,
                custom(tooltipModel: any) {
                    if (tooltipModel.opacity === 0) {
                        setTooltipParams(null);
                        setTooltipItem(null);
                    } else {
                        // @ts-ignore
                        const position = this._chart.canvas.getBoundingClientRect();
                        const tooltipHalfWidth = TOOLTIP_WIDTH_IN_PX / 2;
                        let left = position.left + tooltipModel.caretX;
                        let transform = -50;

                        if (tooltipModel.caretX <= tooltipHalfWidth) {
                            transform = 0;
                        } else if (tooltipHalfWidth >= position.right - (tooltipModel.caretX + position.left)) {
                            transform = -100;
                        }

                        setTooltipParams({
                            x: left,
                            y: position.top + tooltipModel.caretY,
                            transformX: transform,
                        });

                        if (Array.isArray(tooltipModel.dataPoints) && tooltipModel.dataPoints.length) {
                            const point = tooltipModel.dataPoints[0];
                            const item = points[point.index];
                            setTooltipItem(item ?? null);
                        }
                    }
                },
            },
        };
    }, [beginAtZero, points]);

    const plugins = [
        {
            beforeDraw: (instance: any) => {
                if (instance.tooltip._active && instance.tooltip._active.length) {
                    const ctx: CanvasRenderingContext2D = instance.chart.ctx;
                    ctx.save();
                    const activePoint = instance.tooltip._active[0]._model;
                    const area = instance.chartArea;
                    ctx.beginPath();
                    ctx.setLineDash([5, 5]);
                    ctx.moveTo(activePoint.x, area.bottom);
                    ctx.lineTo(activePoint.x, activePoint.y);
                    ctx.strokeStyle = '#192A3E';
                    ctx.lineWidth = 1.2;
                    ctx.stroke();
                }
            },
        },
    ];

    return (
        <div className={cn(styles.container, containerClassName)}>
            <div className={styles.heading}>
                <div>
                    {!!title && <p className={styles.title}>{loading ? <Skeleton width="40%" /> : title}</p>}{' '}
                    {!loading && error && (
                        <p className={styles.errorMessage}>Sorry, we're experiencing technical issues</p>
                    )}
                </div>

                {enableDateRange && (
                    <div className={styles.dateRangeContainer}>
                        <label>From</label>
                        <CustomDatePicker
                            className={styles.dateInput}
                            selectsStart
                            value={dateFrom}
                            onChange={onDateFromChange}
                            startDate={dateFrom}
                            endDate={dateTo}
                            minDate={minDate}
                            maxDate={now}
                            dateFormat="MMM dd, yy"
                        />

                        <label>To</label>
                        <CustomDatePicker
                            className={styles.dateInput}
                            selectsEnd
                            value={dateTo}
                            onChange={onDateToChange}
                            startDate={dateFrom}
                            endDate={dateTo}
                            minDate={dateFrom}
                            maxDate={now}
                            dateFormat="MMM dd, yy"
                        />
                    </div>
                )}
            </div>

            {loading ? (
                <Skeleton baseColor="#f1f1f1" height="10rem" />
            ) : (
                <Line height={height} data={data as any} options={options as any} plugins={plugins} />
            )}

            {tooltipParams &&
                tooltipItem &&
                ReactDOM.createPortal(
                    <div
                        className={styles.tooltip}
                        style={{
                            left: tooltipParams.x + 'px',
                            top: tooltipParams.y + 'px',
                            transform: `translate(${tooltipParams.transformX}%, -115%)`,
                            minWidth: TOOLTIP_WIDTH_IN_PX,
                        }}
                    >
                        {tooltip(tooltipItem)}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default LineChart;
